import useWidgetViewManager from '@/composables/widget-view-manager'
import { convertBase64ToBlob } from '../functions'
import { useOrchestratorApi } from '@/api/orchestrator'
import type { RepeatableContainerApi, InputCountry, InputPhoneNumber, RepeatableContainerAddSectionParams, RepeatableContainerRemoveSectionParams } from 'widget-components/integrations/CustomPage/types'
import type { ExtendedBlock } from '../types'

const useApi = () => ({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sendAction: async (workflowId: string, taskId: string, data: any) => {
        const client = useOrchestratorApi()

        return client.post(`/workflows/${workflowId}/task/${taskId}/page/result`, data)
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    postCustomPageValues: async (workflowId: string, taskId: string, data: any) => {
        const client = useOrchestratorApi()

        return client.post(`/workflows/${workflowId}/custom-pages/${taskId}/values`, data)
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    uploadFile: async ({ blockType, workspaceId, workflowId, partyId, taskId, customPageId, inputDocumentTaskId, fileName, file, fileRef }: any) => {
        const client = useOrchestratorApi()
        const { activeMessage } = useWidgetViewManager()
        const msg = activeMessage.value

        const data = new FormData()
        data.append('file', convertBase64ToBlob(file), fileName)

        const componentTypeSlugs: Record<ExtendedBlock['type'], string> = {
            INPUT_DOCUMENT: 'input-document',
            INPUT_FILE: 'input-file',
        }

        const componentSlug = componentTypeSlugs[blockType] || 'unknown-component'

        const url = msg?.taskType === 'CUSTOM_PAGE_2'
            ? `workflows/${workflowId}/custom-pages/${taskId}/components/${componentSlug}/${inputDocumentTaskId}`
            : `document-upload/${workspaceId}/${partyId}/${taskId}/${customPageId}/${inputDocumentTaskId}`

        try {
            fileRef.progress = .1
            const query = await client.post(
                url,
                data,
                {
                    onUploadProgress: e => {
                        fileRef.progress = e.loaded / (e?.total || 0)
                    },
                },
            )

            const result = {
                ...query.data,
                requireVirusCheck: true,
            }

            fileRef.uploadedData = result
            fileRef.resourceId = query.data.resourceId
            fileRef.progress = 1
            return result

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            fileRef.progress = 1
            const error = e.response?.data || e
            fileRef.error = Array.isArray(error.errors)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ? error.errors.map((err: any) => err.message || err.code).filter(Boolean).join(', ')
                : error.message || error.code

            throw e
        }
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    removeFile: async ({ workspaceId, partyId, taskId, customPageId, inputDocumentTaskId, fileName, resourceId }: any) => {
        const client = useOrchestratorApi()
        return client.delete(
            `document-upload/${
                workspaceId}/${
                partyId}/${
                resourceId}/${
                taskId}/${
                customPageId}/${
                inputDocumentTaskId}/${
                fileName}`,
        )
    },

    downloadDocument: async ({
        componentId,
        resourceId,
        resourceFileName,
    }: {
        componentId?: string
        resourceId: string
        resourceFileName: string
    }) => {
        const { activeMessage } = useWidgetViewManager()
        const msg = activeMessage.value

        const client = useOrchestratorApi()

        const url = msg?.taskType === 'CUSTOM_PAGE_2'
            ? `/workflows/${msg.workflowId}/custom-pages/${msg.taskId}/components/display-document/${componentId}/resources/${resourceId}/${resourceFileName}`
            : `/display-document/${resourceId}/${resourceFileName}`

        return client.get(
            url,
            {
                responseType: 'blob',
            },
        )
    },

    getLazyBlock: async ({ componentId } : { componentId: string }): Promise<{data: ExtendedBlock }> => {
        const client = useOrchestratorApi()
        const { activeMessage } = useWidgetViewManager()

        return client.get(
            `workflows/${activeMessage.value?.workflowId}/custom-pages/${activeMessage.value?.taskId}/components/${componentId}`,
        )
    },

    getInputCountryBlock: async ({ componentId } : { componentId: string }): Promise<{data: InputCountry | InputCountry['items']}> => {
        const client = useOrchestratorApi()
        const { activeMessage } = useWidgetViewManager()

        return client.get(
            `components/input-country/${componentId}`,
            {
                headers: {
                    'task-id': activeMessage.value?.taskId,
                },
            },
        )
    },

    getInputPhoneNumberBlock: ({ componentId } : { componentId: string }) => {
        const client = useOrchestratorApi()
        const { activeMessage } = useWidgetViewManager()

        return client.get<InputPhoneNumber['selectedItems']>(
            `components/input-phone-number/${componentId}`,
            {
                headers: {
                    'task-id': activeMessage.value?.taskId,
                },
            },
        )
    },

    postRepeatableContainerSection: async ({ repeatableContainerId }: RepeatableContainerAddSectionParams) => {
        const client = useOrchestratorApi()
        const { activeMessage } = useWidgetViewManager()

        return client.post<RepeatableContainerApi>(
            `/workflows/${activeMessage.value?.workflowId}/custom-pages/${activeMessage.value?.taskId}/components/repeatable-container/${repeatableContainerId}/sections/`,
        )
    },

    deleteRepeatableContainerSection: ({ repeatableContainerId, sectionId }: RepeatableContainerRemoveSectionParams) => {
        const client = useOrchestratorApi()
        const { activeMessage } = useWidgetViewManager()

        return client.delete(
            `/workflows/${activeMessage.value?.workflowId}/custom-pages/${activeMessage.value?.taskId}/components/repeatable-container/${repeatableContainerId}/sections/${sectionId}`,
        )
    },
})

export default useApi
